/**
 * Pixel-art assets (design system, FOUNDATION — v5 "PIXEL SWEETHEART" §1-2).
 *
 * Crisp pixel-art as SVG grids of squares — self-hosted, CSP 'self', zero
 * external assets. Pixel-art is the SEASONING: these glyphs appear as accents
 * (mascot, brand hearts, action icons, badges, celebrations) while photos,
 * body text and layout stay smooth and readable (v5 §1).
 *
 *  - <PixelGlyph map palette/>  low-level renderer: strings → merged <rect>
 *    runs, shape-rendering:crispEdges. Single-color maps use `X` →
 *    currentColor so glyphs recolor like any icon.
 *  - TICKER — the mascot (v5 §2): a pixel heart-creature with a face. Named
 *    Ticker because that's what a heart is — and this one keeps score.
 *    States: idle (blinks), happy, cheer (sparkles), heartbroken (cracked,
 *    one tear — cute, not grim), sleepy (Zzz), error (sweat), wink.
 *  - Brand hearts: <PixelHeart/> (whole) + <PixelHeartCracked/> (the mark).
 *  - Icon set: star, skull, cross, sparkle, check, flame, bolt.
 *
 * Colors resolve through --px-* tokens (humble.css) with baked fallbacks, so
 * Ticker stays candy-colored in both themes. All animation is CSS-side
 * (.mascot--* / .px-* in humble.css) — freezes under prefers-reduced-motion
 * via the global rule.
 */
import type { SVGProps } from "react";

/* --------------------------------------------------------------- renderer -- */

export type PixelMap = readonly string[];

export interface PixelGlyphProps extends SVGProps<SVGSVGElement> {
  map: PixelMap;
  /** char → CSS color. Default: X → currentColor. '.' and ' ' are empty. */
  palette?: Readonly<Record<string, string>>;
  /** Rendered width in px (height follows the map's aspect). */
  size?: number;
}

const MONO: Readonly<Record<string, string>> = { X: "currentColor" };

/** Merge horizontal runs of the same color into single rects (cheap DOM). */
function runs(map: PixelMap, palette: Readonly<Record<string, string>>) {
  const out: { x: number; y: number; w: number; fill: string }[] = [];
  map.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const c = row[x] ?? ".";
      const fill = palette[c];
      if (!fill) {
        x += 1;
        continue;
      }
      let w = 1;
      while (x + w < row.length && row[x + w] === c) w += 1;
      out.push({ x, y, w, fill });
      x += w;
    }
  });
  return out;
}

export function PixelGlyph({ map, palette = MONO, size = 24, ...rest }: PixelGlyphProps) {
  const h = map.length;
  const w = map[0]?.length ?? 1;
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      width={size}
      height={(size * h) / w}
      shapeRendering="crispEdges"
      aria-hidden="true"
      {...rest}
    >
      {runs(map, palette).map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={1} fill={r.fill} />
      ))}
    </svg>
  );
}

/* ------------------------------------------------------------ brand hearts -- */

const HEART: PixelMap = [
  ".XX...XX.",
  "XXXX.XXXX",
  "XXXXXXXXX",
  "XXXXXXXXX",
  ".XXXXXXX.",
  "..XXXXX..",
  "...XXX...",
  "....X....",
];

/** Still holding together at the tip. That's the whole brand. */
const HEART_CRACKED: PixelMap = [
  ".XX...XX.",
  "XXXX.XXXX",
  "XXX.XXXXX",
  "XXXX.XXXX",
  ".XXX.XXX.",
  "..XX.XX..",
  "...X.X...",
  "....X....",
];

export function PixelHeart(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={HEART} {...props} />;
}
export function PixelHeartCracked(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={HEART_CRACKED} {...props} />;
}

/* --------------------------------------------------------------- icon set -- */

const STAR: PixelMap = [
  "....X....",
  "....X....",
  "...XXX...",
  "XXXXXXXXX",
  ".XXXXXXX.",
  "..XXXXX..",
  "...XXX...",
  "..XX.XX..",
  ".X.....X.",
];

const SKULL: PixelMap = [
  "..XXXXX..",
  ".XXXXXXX.",
  "XXXXXXXXX",
  "XX..X..XX",
  "XX..X..XX",
  "XXXXXXXXX",
  ".XX.X.XX.",
  "..XXXXX..",
];

const CROSS: PixelMap = [
  "XX....XX",
  "XXX..XXX",
  ".XXXXXX.",
  "..XXXX..",
  "..XXXX..",
  ".XXXXXX.",
  "XXX..XXX",
  "XX....XX",
];

const SPARKLE: PixelMap = [
  "...X...",
  "...X...",
  "..XXX..",
  "XXXXXXX",
  "..XXX..",
  "...X...",
  "...X...",
];

const CHECK: PixelMap = [
  "......XX",
  ".....XXX",
  "X...XXX.",
  "XX.XXX..",
  "XXXXX...",
  ".XXX....",
  "..X.....",
];

const FLAME: PixelMap = [
  "...X..",
  "..XX..",
  "..XXX.",
  ".XXXX.",
  "XXXXXX",
  "XXXXXX",
  ".XXXX.",
];

const BOLT: PixelMap = [
  "...XXX",
  "..XXX.",
  ".XXX..",
  "XXXXXX",
  "..XXX.",
  ".XXX..",
  "XXX...",
];

export function PixelStar(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={STAR} {...props} />;
}
export function PixelSkull(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={SKULL} {...props} />;
}
export function PixelCross(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={CROSS} {...props} />;
}
export function PixelSparkle(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={SPARKLE} {...props} />;
}
export function PixelCheck(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={CHECK} {...props} />;
}
export function PixelFlame(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={FLAME} {...props} />;
}
export function PixelBolt(props: Omit<PixelGlyphProps, "map">) {
  return <PixelGlyph map={BOLT} {...props} />;
}

/* ------------------------------------------------------------------ TICKER -- */
/* The mascot. 13×11 body grid; faces are baked per state (hand-merged maps
   read better than layered overlays at this scale).
   Palette: O outline · R body · H highlight · D shade · I ink (face) ·
   B blush · T tear/sweat · Z z's. */

export type TickerState =
  | "idle"
  | "happy"
  | "cheer"
  | "heartbroken"
  | "sleepy"
  | "error"
  | "wink";

const TICKER_PALETTE: Readonly<Record<string, string>> = {
  O: "var(--px-outline, #5c2440)",
  R: "var(--px-body, #ff6d95)",
  H: "var(--px-hi, #ffa9c1)",
  D: "var(--px-shade, #e0446f)",
  I: "var(--px-ink, #351226)",
  B: "var(--px-blush, #ff9db4)",
  T: "var(--px-tear, #7cc9f2)",
  Z: "var(--px-ink, #351226)",
};

/** idle, eyes open — the resting face (frame A). */
const T_IDLE_A: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRIRRRRRIRRO",
  "ORRIRRRRRIRRO",
  ".OBRIRRRIRBO.",
  "..ORRIIIRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

/** idle, mid-blink (frame B) — CSS flips A/B every few seconds. */
const T_IDLE_B: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRRRRRRRRRRO",
  "ORIIRRRRIIRRO",
  ".OBRIRRRIRBO.",
  "..ORRIIIRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

/** happy — ^^ eyes, open smile. */
const T_HAPPY: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRIRRRRRIRRO",
  "ORIRIRRRIRIRO",
  ".OBRIIIIIRBO.",
  "..ORRIIIRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

/** heartbroken — a zigzag crack, one tear, worried mouth. Cute, not grim. */
const T_HEARTBROKEN: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRIRRORRIRRO",
  "ORRIRORRRIRRO",
  ".OBTRRORRRBO.",
  "..ORRIIIRRO..",
  "...ORRORDO...",
  "....ORORO....",
  ".....ORO.....",
  "......O......",
];

/** sleepy — closed eyes, tiny mouth (the Zzz float in the sprite frame). */
const T_SLEEPY: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRRRRRRRRRRO",
  "ORIIRRRRIIRRO",
  ".OBRRRIRRRBO.",
  "..ORRRRRRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

/** error — wide eyes, zigzag mouth, one bead of sweat. */
const T_ERROR: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRTO.",
  "ORHRRRORRRTRO",
  "ORRIRRRRRIRRO",
  "ORRIRRRRRIRRO",
  ".OBRIRIRIRBO.",
  "..ORRIRIRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

/** wink — the recovery frame after the shatter beat. It's fine. It's a bit. */
const T_WINK: PixelMap = [
  "..OOO...OOO..",
  ".ORHRO.ORRRO.",
  "ORHRRRORRRRRO",
  "ORRIRRRRRRRRO",
  "ORRIRRRRIIIRO",
  ".OBRIRRRIRBO.",
  "..ORRIIIRRO..",
  "...ORRRRDO...",
  "....ORRDO....",
  ".....ORO.....",
  "......O......",
];

const TICKER_FACES: Record<Exclude<TickerState, "idle">, PixelMap> = {
  happy: T_HAPPY,
  cheer: T_HAPPY,
  heartbroken: T_HEARTBROKEN,
  sleepy: T_SLEEPY,
  error: T_ERROR,
  wink: T_WINK,
};

/** Mini decorations rendered inside the sprite frame. */
const MINI_Z: PixelMap = ["XXX", ".X.", "XXX"];
const MINI_SPARK: PixelMap = [".X.", "XXX", ".X."];

function Cells({ map, palette, tx, ty }: { map: PixelMap; palette: Readonly<Record<string, string>>; tx: number; ty: number }) {
  return (
    <g transform={`translate(${tx} ${ty})`}>
      {runs(map, palette).map((r, i) => (
        <rect key={i} x={r.x} y={r.y} width={r.w} height={1} fill={r.fill} />
      ))}
    </g>
  );
}

export interface TickerSpriteProps extends SVGProps<SVGSVGElement> {
  state?: TickerState;
  /** Rendered width in px of the whole sprite frame. */
  size?: number;
}

/**
 * Ticker, the pixel sweetheart. One SVG, crispEdges; the idle state carries
 * two stacked frames (open/blink) toggled by CSS (.px-frame-*); sleepy adds
 * Zzz and cheer adds sparkles (.px-zzz / .px-spark, CSS-animated).
 */
export function TickerSprite({ state = "idle", size = 64, ...rest }: TickerSpriteProps) {
  // frame: 21 wide × 15 tall — body at (4,3), headroom for Zzz / sparkles
  const W = 21;
  const H = 15;
  const ink = TICKER_PALETTE.Z ?? "var(--px-ink, #351226)";
  const spark = "var(--px-spark, #ffd76e)";
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={size}
      height={(size * H) / W}
      shapeRendering="crispEdges"
      aria-hidden="true"
      {...rest}
    >
      {state === "idle" ? (
        <>
          <g className="px-frame-a">
            <Cells map={T_IDLE_A} palette={TICKER_PALETTE} tx={4} ty={3} />
          </g>
          <g className="px-frame-b">
            <Cells map={T_IDLE_B} palette={TICKER_PALETTE} tx={4} ty={3} />
          </g>
        </>
      ) : (
        <Cells map={TICKER_FACES[state]} palette={TICKER_PALETTE} tx={4} ty={3} />
      )}
      {state === "sleepy" && (
        <>
          <g className="px-zzz px-zzz--1">
            <Cells map={MINI_Z} palette={{ X: ink }} tx={17} ty={4} />
          </g>
          <g className="px-zzz px-zzz--2">
            <Cells map={MINI_Z} palette={{ X: ink }} tx={15} ty={0} />
          </g>
        </>
      )}
      {state === "cheer" && (
        <>
          <g className="px-spark px-spark--1">
            <Cells map={MINI_SPARK} palette={{ X: spark }} tx={0} ty={2} />
          </g>
          <g className="px-spark px-spark--2">
            <Cells map={MINI_SPARK} palette={{ X: spark }} tx={18} ty={1} />
          </g>
          <g className="px-spark px-spark--3">
            <Cells map={MINI_SPARK} palette={{ X: spark }} tx={16} ty={11} />
          </g>
        </>
      )}
      {state === "error" && (
        <g className="px-spark px-spark--1">
          <Cells map={MINI_SPARK} palette={{ X: "var(--px-tear, #7cc9f2)" }} tx={1} ty={1} />
        </g>
      )}
    </svg>
  );
}
